/**
 * Sentry init + helpers — opt-in via REACT_APP_SENTRY_DSN env var.
 * When unset, every export is a safe no-op (no network, no overhead).
 *
 * To enable in production:
 *   1. Sign up at sentry.io and create a React project
 *   2. Add REACT_APP_SENTRY_DSN=https://...@sentry.io/... to /app/frontend/.env
 *   3. Optionally tune REACT_APP_SENTRY_TRACES_SAMPLE_RATE (default 0.1)
 *   4. Optionally set REACT_APP_SENTRY_ENVIRONMENT (defaults to NODE_ENV)
 */
import * as Sentry from '@sentry/react';
import React from 'react';

const DSN = process.env.REACT_APP_SENTRY_DSN;
export const SENTRY_ENABLED = Boolean(DSN);

if (SENTRY_ENABLED) {
  Sentry.init({
    dsn: DSN,
    environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || process.env.NODE_ENV,
    tracesSampleRate: parseFloat(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || '0.1'),
    sendDefaultPii: false,
    // Filter dev-only React warnings + browser-extension noise out
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection captured',
      'Network Error',
    ],
  });
}

/**
 * captureError(err, context?) — safe wrapper around Sentry.captureException.
 * Logs to console.error when Sentry is disabled so devs still see the error.
 */
export const captureError = (err, context) => {
  if (SENTRY_ENABLED) {
    Sentry.captureException(err, context ? { contexts: { extra: context } } : undefined);
  } else {
    // eslint-disable-next-line no-console
    console.error('[captureError]', err, context || '');
  }
};

/**
 * AppErrorBoundary — wraps the whole React tree. Catches unhandled render
 * errors. Renders a polished fallback UI; reports to Sentry when enabled.
 */
const FallbackUI = ({ resetError }) => (
  <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center px-6">
    <div className="max-w-md w-full text-center">
      <div className="inline-block px-3 py-1 mb-6 text-[10px] uppercase tracking-widest text-[#d4af37] border border-[#d4af37]/30">
        Unexpected error
      </div>
      <h1 className="font-playfair text-3xl md:text-4xl text-white mb-3">
        Something went sideways.
      </h1>
      <p className="text-slate-400 text-sm leading-relaxed mb-6">
        Our team has been notified automatically. Try refreshing — if this persists,
        contact us at <a href="mailto:support@ibclub.org" className="text-[#d4af37] hover:underline">support@ibclub.org</a>.
      </p>
      <div className="flex justify-center gap-3">
        <button
          onClick={resetError}
          className="bg-[#d4af37] hover:bg-[#e5c558] text-slate-950 font-semibold px-5 py-2 text-sm"
          data-testid="error-boundary-retry"
        >
          Try again
        </button>
        <a
          href="/"
          className="border border-white/20 hover:bg-slate-800 text-slate-200 font-semibold px-5 py-2 text-sm"
          data-testid="error-boundary-home"
        >
          Back home
        </a>
      </div>
    </div>
  </div>
);

export const AppErrorBoundary = ({ children }) => (
  <Sentry.ErrorBoundary fallback={({ resetError }) => <FallbackUI resetError={resetError} />}>
    {children}
  </Sentry.ErrorBoundary>
);

export default Sentry;
